import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bulkApi, type BulkRequest } from "../lib/api";
import { ScenarioExpiryWarning } from "./ScenarioExpiryWarning";
import type { ScenarioPoolMember } from "../lib/api";
import { useConfirmDialog } from "../components/ConfirmDialog.helpers";

const terminal = (status?: string) => ["succeeded", "failed", "dead", "stopped"].includes(status || "");
const button = "rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50";

export function BulkScenariosPanel({ teamId, endDate, days, members }: {
  teamId: string; endDate: string; days: number; members: ScenarioPoolMember[];
}) {
  const client = useQueryClient();
  const confirm = useConfirmDialog();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [includeTeam, setTeam] = useState(true);
  const [includePeople, setPeople] = useState(false);
  const [teamCount, setTeamCount] = useState(5);
  const [personCount, setPersonCount] = useState(5);
  const [ids, setIds] = useState<string[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const requestRef = useRef<{ signature: string; id: string } | null>(null);
  const latestKey = ["scenario-bulk-latest", teamId, endDate, days];
  const latest = useQuery({
    queryKey: latestKey,
    queryFn: () => bulkApi.latest(teamId, endDate, days),
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const activeId = batchId ?? latest.data?.batch_id ?? null;
  const statusKey = ["scenario-bulk-status", teamId, activeId];
  const status = useQuery({
    queryKey: statusKey, queryFn: () => bulkApi.status(teamId, activeId!),
    enabled: !!activeId, placeholderData: undefined,
    refetchInterval: (q) => q.state.error || terminal(q.state.data?.status) ? false : 2000,
  });
  const data = activeId ? status.data : undefined;
  // A batch restored on load is the record of what was asked for, so the form
  // shows its settings, not the defaults. A batch started here already matches.
  const [restored, setRestored] = useState<string | null>(null);
  if (!batchId && data && data.batch_id !== restored) {
    setRestored(data.batch_id);
    const team = data.targets.find(t => t.kind === "team");
    const people = data.targets.filter(t => t.kind === "individual");
    const person = people[0];
    setTeam(!!team);
    if (team) setTeamCount(team.requested);
    setPeople(!!person);
    if (person) setPersonCount(person.requested);
    setIds(people.flatMap(t => t.agent_id ? [t.agent_id] : []));
  }
  useEffect(() => {
    if (data?.status === "stopped") requestRef.current = null;
  }, [data?.status]);
  const running = !!activeId && !status.isError && (!data || !terminal(data.status));
  const max = (includeTeam ? teamCount : 0) + (includePeople ? ids.length * personCount : 0);
  const valid = (includeTeam || includePeople) && (!includePeople || ids.length > 0)
    && [teamCount, personCount].every(n => Number.isInteger(n) && n >= 1 && n <= 5)
    && ids.every(id => members.some(m => m.dialpad_user_id === id));
  async function action(work: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try { await work(); }
    catch (e) { if (mounted.current) setError((e as { message?: string }).message || "The request failed."); }
    finally { busyRef.current = false; if (mounted.current) setBusy(false); }
  }
  async function generate() {
    const body = { end_date: endDate, lookback_days: days, include_team: includeTeam,
      team_count: teamCount, include_individuals: includePeople, individual_count: personCount,
      agent_ids: includePeople ? ids : [] };
    const signature = JSON.stringify(body);
    if (requestRef.current?.signature !== signature) requestRef.current = { signature, id: crypto.randomUUID() };
    const result = await bulkApi.create(teamId, { ...body, request_id: requestRef.current.id } satisfies BulkRequest);
    if (mounted.current) {
      requestRef.current = null;
      setBatchId(result.batch_id);
      client.setQueryData(latestKey, result);
    }
  }
  async function download(target?: string, index?: number) {
    const file = await bulkApi.download(teamId, activeId!, target, index);
    if (!mounted.current) return;
    const url = URL.createObjectURL(file.blob);
    const a = document.createElement("a"); a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  }
  const count = (label: string, value: number, set: (value: number) => void) => (
    <label className="block text-sm">{label}
      <select aria-label={label} className="ml-3 rounded border bg-background p-1" value={value} onChange={e => set(Number(e.target.value))}>
        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </label>
  );
  return <section className="space-y-4 rounded-lg border border-border bg-card p-6">
    <h2 className="font-semibold">Bulk scenarios</h2>
    <p className="text-sm text-muted-foreground">The team uses everyone’s calls. Each checked person is analysed separately. Only supported scenarios are generated, in suggestion order.</p>
    <fieldset disabled={busy || running} className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-3 rounded border border-border p-3">
        <label><input type="checkbox" checked={includeTeam} onChange={e => setTeam(e.target.checked)} /> Include Team</label>
        {includeTeam && count("Team scenarios", teamCount, setTeamCount)}
      </div>
      <div className="space-y-3 rounded border border-border p-3">
        <label><input type="checkbox" checked={includePeople} onChange={e => setPeople(e.target.checked)} /> Include Individuals</label>
        {includePeople && <>
          {count("Scenarios per person", personCount, setPersonCount)}
          {members.map(m => <label className="block text-sm" key={m.dialpad_user_id}>
            <input type="checkbox" checked={ids.includes(m.dialpad_user_id)}
              onChange={e => setIds(old => e.target.checked ? [...old, m.dialpad_user_id] : old.filter(id => id !== m.dialpad_user_id))} /> {m.name}
          </label>)}
        </>}
      </div>
    </fieldset>
    <p className="text-sm">Up to {max} files: {includeTeam ? teamCount : 0} team + {includePeople ? ids.length : 0} people × {personCount} each.</p>
    {!valid && <p className="text-sm">Include at least one target, and select at least one person when Individuals is included.</p>}
    <button className={button} disabled={!valid || busy || running || latest.isPending || latest.isError}
      onClick={() => void action(generate)}>{busy || running ? "Generating…" : "Generate"}</button>
    {(error || latest.error || status.error) && <p role="alert" className="text-sm text-destructive">{error || (latest.error || status.error)?.message}</p>}
    {data && <>
      <h3 className="font-semibold">Saved batch results</h3>
      <ScenarioExpiryWarning expiresAt={data.expires_at} />
      <p className="text-sm" aria-live="polite">Batch: {data.status}. You can leave and return; successful files are saved.</p>
      {data.status === "stopped" && <p className="text-sm">Remaining work was stopped. Finished files are kept. Generate starts a new batch.</p>}
      {data.error && <p role="alert">{data.error}</p>}
      <div className="flex flex-wrap gap-2">
        <button className={button} disabled={busy || !data.targets.some(t => t.generated > 0)}
          onClick={() => void action(() => download())}>Download all successful outputs (ZIP)</button>
        {running && <button className={button} disabled={busy} onClick={() => void action(async () => {
          await bulkApi.stop(teamId, activeId!);
          requestRef.current = null;
          await client.cancelQueries({ queryKey: statusKey });
          client.setQueryData(statusKey, { ...data, status: "stopped", error: null });
          await client.invalidateQueries({ queryKey: statusKey });
        })}>Stop remaining work (keep files)</button>}
        {data.status !== "stopped" && terminal(data.status) && (data.targets.some(t => t.status !== "complete") || data.status !== "succeeded") &&
          <button className={button} disabled={busy} onClick={() => void action(async () => {
            await bulkApi.retry(teamId, activeId!);
            await client.invalidateQueries({ queryKey: statusKey });
          })}>Retry failed work</button>}
        <button className={button} disabled={busy} onClick={() => void action(async () => {
          const ok = await confirm.confirm({ title: "Clear bulk scenarios?", message: "This deletes this batch’s reports and files and stops queued work from restoring them. Source calls and transcripts are not deleted.", confirmLabel: "Delete batch", destructive: true });
          if (!ok || !mounted.current) return;
          await bulkApi.clear(teamId, activeId!);
          await client.cancelQueries({ queryKey: latestKey });
          await client.cancelQueries({ queryKey: statusKey });
          client.setQueryData(latestKey, { batch_id: null });
          client.removeQueries({ queryKey: statusKey });
          if (mounted.current) { setBatchId(null); requestRef.current = null; }
        })}>Clear bulk scenarios</button>
      </div>
      {data.targets.map(t => <article key={t.id} className="space-y-2 rounded border border-border p-4">
        <h3 className="font-semibold">{t.kind === "team" ? "Team" : "Individual"} — {t.name}</h3>
        <p className="text-sm">Requested: {t.requested} · Available: {t.available ?? "not yet known"} · Successfully generated: {t.generated} · {t.status}</p>
        {t.notes.map((note, i) => <p className="text-sm text-muted-foreground" key={i}>{note}</p>)}
        {t.error && <p role="alert" className="text-sm text-destructive">{t.error}</p>}
        {t.outputs.map(o => <div key={o.index} className="text-sm">
          {o.status === "ready" ? <button className={button} disabled={busy} onClick={() => void action(() => download(t.id, o.index))}>Download {t.name} scenario {o.index + 1}</button>
            : `Scenario ${o.index + 1}: ${o.status}${o.error ? ` — ${o.error}` : ""}`}
        </div>)}
      </article>)}
    </>}
    {confirm.element}
  </section>;
}
